import * as XLSX from 'xlsx';

/**
 * Parse a PeopleSoft class-roster export into a normalized student list.
 *
 * PeopleSoft "Download to Excel" produces an HTML <table> saved with an .xls
 * extension (not a real Excel binary). SheetJS reads that HTML table as well as
 * genuine .xlsx/.xls/.csv files, so faculty can upload whatever they have.
 *
 * Relevant columns (matched case-insensitively, resilient to reordering):
 *   - "ID"          -> studentId        (e.g. "60100086")
 *   - "Name"        -> "Last,First M"   reformatted to "First M Last"
 *   - "Status Note" -> "Withdrawn" rows are flagged (caller decides inclusion)
 *
 * @param {ArrayBuffer} data - raw file bytes
 * @returns {{ students: Array<{studentId:string,name:string,status:string,withdrawn:boolean}>, total:number, withdrawn:number }}
 */
export function parsePeopleSoftRoster(data) {
  const workbook = XLSX.read(data, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error('The file does not contain any worksheets.');
  }

  const sheet = workbook.Sheets[sheetName];
  // header:1 -> array of rows, each an array of cell strings. raw:false keeps
  // IDs as strings so leading characters / formatting are never coerced away.
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });

  const headerIndex = rows.findIndex((row) => {
    const cells = row.map((c) => String(c).trim().toLowerCase());
    return cells.includes('id') && cells.includes('name');
  });

  if (headerIndex === -1) {
    throw new Error('Could not find an "ID" and "Name" header row. Is this a PeopleSoft class roster export?');
  }

  const header = rows[headerIndex].map((c) => String(c).trim().toLowerCase());
  const idCol = header.indexOf('id');
  const nameCol = header.indexOf('name');
  const statusCol = header.findIndex((c) => c === 'status note' || c === 'status');

  const seen = new Set();
  const students = [];
  let withdrawn = 0;

  for (let i = headerIndex + 1; i < rows.length; i++) {
    const row = rows[i];
    const studentId = String(row[idCol] ?? '').trim();
    if (!studentId || seen.has(studentId)) continue;
    seen.add(studentId);

    const rawName = String(row[nameCol] ?? '').trim();
    const status = statusCol === -1 ? '' : String(row[statusCol] ?? '').trim();
    const isWithdrawn = /withdraw/i.test(status);
    if (isWithdrawn) withdrawn++;

    students.push({
      studentId,
      name: reformatName(rawName),
      status,
      withdrawn: isWithdrawn,
    });
  }

  return { students, total: students.length, withdrawn };
}

/**
 * Convert PeopleSoft "Last,First Middle" to "First Middle Last".
 * Splits on the first comma only so multi-part last names survive
 * ("Abu Hantash,Maha" -> "Maha Abu Hantash"). Names without a comma are
 * returned unchanged.
 */
export function reformatName(name) {
  if (!name) return '';
  const comma = name.indexOf(',');
  if (comma === -1) return name.trim();
  const last = name.slice(0, comma).trim();
  const rest = name.slice(comma + 1).trim();
  return rest ? `${rest} ${last}` : last;
}
